/**
 * Canvas state and template utilities for canvas interactions
 * Extracted from MyCanvas.js to reduce file size
 */

/**
 * Handles image resize operation
 * @param {string} id - Image ID
 * @param {number} width - New width
 * @param {number} height - New height
 * @param {Function} updateImageProperties - Function to update image properties
 * @param {Function} setHasUnsavedChanges - Function to set unsaved changes flag
 */
export const handleImageResize = (
  id,
  width,
  height,
  updateImageProperties,
  setHasUnsavedChanges
) => {
  updateImageProperties(id, { width, height });

  // Mark as having unsaved changes - this ensures the changes will be saved
  setHasUnsavedChanges(true);

  // saveToHistory() is now called inside updateImageProperties
};

/**
 * Clears saved canvas state from localStorage and IndexedDB
 * @param {string} pageId - Page ID to clear
 * @param {Function} openCanvasDB - Function to open IndexedDB
 * @param {Function} setHasUnsavedChanges - Function to set unsaved changes flag
 */
export const clearSavedCanvasState = (
  pageId,
  openCanvasDB,
  setHasUnsavedChanges
) => {
  localStorage.removeItem("canvasState");
  localStorage.removeItem("canvasSnapshot");
  sessionStorage.removeItem("canvasStateInfo");

  const clearIndexedDB = async () => {
    if (!window.indexedDB) return;

    try {
      const db = await openCanvasDB();

      // Delete from canvasElements store by pageId
      if (db.objectStoreNames.contains("canvasElements")) {
        await new Promise((resolve, reject) => {
          const transaction = db.transaction(["canvasElements"], "readwrite");
          const store = transaction.objectStore("canvasElements");
          const request = store.delete(pageId);

          request.onsuccess = () => resolve();
          request.onerror = (event) => {
            reject(event.target.error);
          };
        });
      }

      // Delete from canvasMarkers store by pageId
      if (db.objectStoreNames.contains("canvasMarkers")) {
        await new Promise((resolve, reject) => {
          const transaction = db.transaction(["canvasMarkers"], "readwrite");
          const store = transaction.objectStore("canvasMarkers");
          const request = store.delete(pageId); // use pageId instead of "currentMarkers"

          request.onsuccess = () => resolve();
          request.onerror = (event) => {
            reject(event.target.error);
          };
        });
      }
    } catch (error) {}
  };

  clearIndexedDB().catch((error) => {});

  setHasUnsavedChanges(false); // reset UI flag
};

const LINE_LIKE_TYPES = new Set([
  "line",
  "arrow",
  "doublesidedarrow",
  "double_sided_arrow",
]);

export function isLineLikeElementType(type) {
  if (type == null) return false;
  return LINE_LIKE_TYPES.has(String(type).trim().toLowerCase());
}

/**
 * Polyline points may live on the root, shape_info, or shape_properties. Root `points`
 * can be null after merges — do not treat that as canonical when nested arrays exist.
 */
export function resolveLineArrowPoints(element) {
  if (!element || typeof element !== "object") return null;
  const isValidPoints = (arr) => Array.isArray(arr) && arr.length >= 4;
  const isDegeneratePoints = (arr) => {
    if (!isValidPoints(arr)) return true;
    let totalLength = 0;
    for (let i = 0; i < arr.length - 2; i += 2) {
      const x1 = Number(arr[i]) || 0;
      const y1 = Number(arr[i + 1]) || 0;
      const x2 = Number(arr[i + 2]) || 0;
      const y2 = Number(arr[i + 3]) || 0;
      totalLength += Math.hypot(x2 - x1, y2 - y1);
    }
    return totalLength < 0.5;
  };
  const isLikelyDefaultPoints = (arr) => {
    if (!isValidPoints(arr) || arr.length !== 4) return false;
    const [x1, y1, x2, y2] = arr.map((v) => Number(v) || 0);
    return (
      (Math.abs(x1) < 0.001 &&
        Math.abs(y1) < 0.001 &&
        Math.abs(x2 - 100) < 0.001 &&
        Math.abs(y2) < 0.001) ||
      (Math.abs(x1) < 0.001 &&
        Math.abs(y1) < 0.001 &&
        Math.abs(x2 - 100) < 0.001 &&
        Math.abs(y2 - 100) < 0.001)
    );
  };

  const rootPoints = isValidPoints(element.points) ? element.points : null;
  const infoPoints = isValidPoints(element.shape_info?.points)
    ? element.shape_info.points
    : null;
  const propsPoints = isValidPoints(element.shape_properties?.points)
    ? element.shape_properties.points
    : null;
  const rootUsable = rootPoints && !isDegeneratePoints(rootPoints);
  const infoUsable = infoPoints && !isDegeneratePoints(infoPoints);
  const propsUsable = propsPoints && !isDegeneratePoints(propsPoints);

  // Match CanvasPageRenderer behavior:
  // if root points are generic placeholders, prefer nested real geometry.
  if (
    isLikelyDefaultPoints(rootPoints) ||
    (rootPoints && !rootUsable && (infoUsable || propsUsable))
  ) {
    if (infoUsable) return infoPoints;
    if (propsUsable) return propsPoints;
  }
  if (rootUsable) return rootPoints;
  if (infoUsable) return infoPoints;
  if (propsUsable) return propsPoints;
  if (rootPoints) return rootPoints;
  if (infoPoints) return infoPoints;
  if (propsPoints) return propsPoints;
  return null;
}

export function shouldHydrateLineLikePoints(el) {
  if (!el || typeof el !== "object") return false;
  if (isLineLikeElementType(el.type)) return true;
  return (
    el.hasStartArrow === true ||
    el.hasEndArrow === true ||
    el.pointerAtBeginning === true ||
    el.pointerAtEnd === true ||
    el.pointerAtEnding === true
  );
}

/** Before IndexedDB write: ensure root + shape_info carry a valid points array when possible. */
export function hydrateLineArrowPointsForPersist(el) {
  if (!shouldHydrateLineLikePoints(el)) return el;
  const pts = resolveLineArrowPoints(el);
  if (!pts || pts.length < 4) return el;
  const next = { ...el, points: pts };
  if (next.shape_info && typeof next.shape_info === "object") {
    next.shape_info = { ...next.shape_info, points: pts };
  }
  return next;
}

/**
 * Preserves template structure for canvas elements
 * @param {Array} elements - Array of canvas elements
 * @returns {Array} Elements with preserved template structure
 */
export const preserveTemplateStructure = (elements) => {
  return elements.map((element) => {
    // Check if this element came from a template by looking for shape_info structure
    // Template elements have properties at root level due to spreading in loadCanvasElements
    // We need to reconstruct the shape_info structure for template elements
    if (element.id && element.type && element.shape_info) {
      const lineLike = isLineLikeElementType(element.type);
      const mergedLinePoints = lineLike ? resolveLineArrowPoints(element) : null;
      const updatedShapeInfo = {
        ...element.shape_info,
        // Use root level properties if they exist, otherwise fall back to shape_info
        x: element.x !== undefined ? element.x : element.shape_info.x,
        y: element.y !== undefined ? element.y : element.shape_info.y,
        width:
          element.width !== undefined
            ? element.width
            : element.shape_info.width,
        height:
          element.height !== undefined
            ? element.height
            : element.shape_info.height,
        fillColor:
          element.fillColor !== undefined
            ? element.fillColor
            : element.shape_info.fillColor,
        strokeColor:
          element.strokeColor !== undefined
            ? element.strokeColor
            : element.shape_info.strokeColor,
        strokeWidth:
          element.strokeWidth !== undefined
            ? element.strokeWidth
            : element.shape_info.strokeWidth,
        points:
          lineLike && mergedLinePoints
            ? mergedLinePoints
            : element.points != null
              ? element.points
              : element.shape_info.points,
        radius:
          element.radius !== undefined
            ? element.radius
            : element.shape_info.radius,
        stroke:
          element.stroke !== undefined
            ? element.stroke
            : element.shape_info.stroke,
      };

      return {
        ...element,
        ...(lineLike && mergedLinePoints ? { points: mergedLinePoints } : {}),
        shape_info: updatedShapeInfo,
      };
    } else if (
      element.id &&
      element.type &&
      (element.x !== undefined || element.y !== undefined)
    ) {
      const lineLike = isLineLikeElementType(element.type);
      const mergedLinePoints = lineLike ? resolveLineArrowPoints(element) : null;
      const {
        x,
        y,
        width,
        height,
        fillColor,
        strokeColor,
        strokeWidth,
        points,
        radius,
        stroke,
        ...otherProps
      } = element;

      const shapeInfoPoints = lineLike && mergedLinePoints
        ? mergedLinePoints
        : Array.isArray(points) && points.length > 0
          ? points
          : [];

      return {
        id: element.id,
        type: element.type,
        shape_info: {
          x: x || 0,
          y: y || 0,
          width: width || 100,
          height: height || 100,
          fillColor: fillColor || "#ffffff",
          strokeColor: strokeColor || "#000000",
          strokeWidth: strokeWidth || 2,
          points: shapeInfoPoints,
          radius: radius || 50,
          stroke: stroke || strokeColor || "#000000",
          ...otherProps,
        },
        // Keep the current properties at root level for compatibility
        ...element,
        ...(lineLike && mergedLinePoints ? { points: mergedLinePoints } : {}),
      };
    }
    return element;
  });
};
